import React, { useEffect, useState } from 'react';
import styles from './Project360QuestionnairesGrid.module.scss';
import AdminProjectLayout from '../../../project/AdminProjectLayout/AdminProjectLayout';
import { useTranslate } from 'react-translate.ts';
import { useDispatch, useSelector } from 'react-redux';
import Admin360QuestionnaireSlice from '../../../../slices/Admin360QuestionnaireSlice';
import AdminQuestionnaireCard from '../../../card/AdminQuestionnaireCard/AdminQuestionnaireCard';
import ProjectQuestionnaire from '../../../../types/project/ProjectQuestionnaire';
import { generatePath, useNavigate } from 'react-router-dom';
import {
    ADMIN_PROJECT_CREATE_PATH,
    ADMIN_PROJECT_QUESTIONNAIRES_DETAIL_PATH,
} from '../../../../../constants/route.constants';
import { ButtonVariants, Skeleton } from '@tcc-online/common-components';
import AdminDashboardActions from '../../../../pages/AdminDashboardPage/components/AdminDashboardActions/AdminDashboardActions';
import FileEarmarkPlusFillIcon from 'react-bootstrap-icons/dist/icons/file-earmark-plus-fill';
import { Formik } from 'formik';
import { FormInput, FormSelect } from '@tcc-online/input-components';
import CreateDialog from '../../../form/CreateDialog/CreateDialog';
import { AppDispatch } from '../../../../../reducers/store';
import FormItem from '../../../form/FormItem/FormItem';
import { LanguageService } from '../../../../../services';
import allLocales from '../../../../../config/locales.json';
import Pagination from '../../../../../common/components/Pagination/Pagination';
import usePagination from '../../../../../common/hooks/usePagination';
import FullSizePageLayout from '../../../../../common/components/FullSizePageLayout/FullSizePageLayout';
import useAdminProjectBreadcrumbs, {
    BreadcrumbFlag,
} from '../../../../hooks/useAdminProjectBreadcrumbs';
import { isSuccessfulResponse } from '../../../../../helpers';

const skeleton = Array.from(new Array(12), (_, index) => (
    <Skeleton key={index} height={250} className={styles.skeleton} />
));
const breadcrumbFlags =
    BreadcrumbFlag.Products |
    BreadcrumbFlag.Projects |
    BreadcrumbFlag.Questionnaires;

export default function Project360QuestionnairesGrid() {
    const translate = useTranslate();
    const { data, loading, pagination } = useSelector(
        Admin360QuestionnaireSlice.state.list,
    );
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { page, changePage } = usePagination();
    const breadcrumbs = useAdminProjectBreadcrumbs(breadcrumbFlags);

    useEffect(() => {
        dispatch(
            Admin360QuestionnaireSlice.actions.getQuestionnaires({ page }),
        );
    }, [dispatch, page]);

    return (
        <FullSizePageLayout breadcrumbs={breadcrumbs}>
            <AdminProjectLayout
                classNames={{ heading: styles.heading }}
                heading={
                    <>
                        <div>{translate('Dotazníky')}</div>
                        <AdminDashboardActions>
                            <AdminDashboardActions.Action
                                text={translate('Přidat dotaznik')}
                                onClick={() => setShowCreateDialog(true)}
                                buttonProps={{
                                    variant: ButtonVariants.positive,
                                }}
                            >
                                <FileEarmarkPlusFillIcon />
                            </AdminDashboardActions.Action>
                        </AdminDashboardActions>
                    </>
                }
            >
                <Formik<Values>
                    initialValues={{
                        [Fields.name]: '',
                        [Fields.primaryLanguage]: '',
                    }}
                    onSubmit={submit}
                >
                    <FormDialog
                        isVisible={showCreateDialog}
                        isLoading={loading}
                        onClose={close}
                    />
                </Formik>
                <div className={styles.grid}>
                    {!loading
                        ? data.map((questionnaire: ProjectQuestionnaire) => (
                              <AdminQuestionnaireCard
                                  key={questionnaire.id}
                                  questionnaire={questionnaire}
                              />
                          ))
                        : skeleton}
                </div>
                <Pagination
                    page={page}
                    pages={pagination?.pageCount || 0}
                    nextPage={pagination?.nextPage}
                    prevPage={pagination?.prevPage}
                    onChange={changePage}
                    classNames={{ pagination: styles.pagination }}
                    disabled={loading}
                />
            </AdminProjectLayout>
        </FullSizePageLayout>
    );

    async function submit(values: Values) {
        const response = await dispatch(
            Admin360QuestionnaireSlice.actions.createQuestionnaire({
                name: values.name,
                primaryLanguage: values.primaryLanguage,
            }),
        ).unwrap();

        if (isSuccessfulResponse(response.status)) {
            close();

            if (response.data?.id !== null && response.data?.id !== undefined) {
                navigate(
                    generatePath(
                        `${ADMIN_PROJECT_QUESTIONNAIRES_DETAIL_PATH}/${ADMIN_PROJECT_CREATE_PATH}`,
                        {
                            questionnaireId: response.data.id.toString(),
                            '*': '',
                        },
                    ),
                );
            }
        }
    }

    function close() {
        setShowCreateDialog(false);
    }
}

interface Values {
    [Fields.name]: string;
    [Fields.primaryLanguage]: string;
}

interface FieldsType {
    name: 'name';
    primaryLanguage: 'primaryLanguage';
}

const Fields: FieldsType = {
    name: 'name',
    primaryLanguage: 'primaryLanguage',
};

type FormDialogProps = {
    isVisible: boolean;
    isLoading?: boolean;
    onClose: () => void;
};

const langOptions: string[] = Object.keys(allLocales);

function FormDialog(props: FormDialogProps) {
    const translate = useTranslate();

    return (
        <CreateDialog
            {...props}
            heading={translate('Vytvorit dotaznik')}
            classNames={{ card: styles.dialog }}
        >
            <FormItem label={translate('Nazev dotazniku')} name={Fields.name}>
                <FormInput name={Fields.name} />
            </FormItem>
            <FormItem
                label={translate('Jazyk škály')}
                name={Fields.primaryLanguage}
            >
                <FormSelect<string>
                    name={Fields.primaryLanguage}
                    options={langOptions}
                    getOptionValue={(option: string) => option}
                    getOptionLabel={(option: string) =>
                        LanguageService.getLanguageName(option)
                    }
                />
            </FormItem>
        </CreateDialog>
    );
}
